import type { RefObject } from 'react';
import { useEffect } from 'react';
import {
  createArcadeStorageResponseMessage,
  getArcadeStorageNamespaceKey,
  isArcadeStorageJson,
  parseArcadeStorageRequestMessage,
} from '@/lib/arcade-storage';

interface UseArcadeStorageBridgeOptions {
  iframeRef: RefObject<HTMLIFrameElement | null>;
  iframeOrigin: string;
  challengeId: string | null | undefined;
  gameId: string | null | undefined;
}

// Answers the embed's window.platanusArcadeStorage bridge (get/set/remove) by
// backing it with namespaced localStorage. Without a parent handling these
// requests the embed's bridge rejects with "Arcade storage bridge timed out",
// so every page that hosts the arcade iframe must run this (game-player has its
// own copy of this logic; the machine page uses this hook).
export function useArcadeStorageBridge({
  iframeRef,
  iframeOrigin,
  challengeId,
  gameId,
}: UseArcadeStorageBridgeOptions) {
  useEffect(() => {
    if (!challengeId || !gameId) {
      return;
    }

    // Sandboxed iframes without allow-same-origin have an opaque origin, so the
    // post target must be '*' in production (matches the embed's own checks).
    const isDevelopmentEnvironment = import.meta.env.DEV;
    const postTarget = isDevelopmentEnvironment ? iframeOrigin : '*';

    function postStorageResponse(
      response: ReturnType<typeof createArcadeStorageResponseMessage>,
    ) {
      iframeRef.current?.contentWindow?.postMessage(response, postTarget);
    }

    function handleStorageRequestMessage(event: MessageEvent<unknown>) {
      const isAllowedIframeOrigin =
        event.origin === iframeOrigin ||
        (!isDevelopmentEnvironment && event.origin === 'null');

      if (
        event.source !== iframeRef.current?.contentWindow ||
        !isAllowedIframeOrigin
      ) {
        return;
      }

      const request = parseArcadeStorageRequestMessage(event.data);
      if (!request) {
        return;
      }

      const storageKey = getArcadeStorageNamespaceKey({
        challengeId: challengeId as string,
        gameId: gameId as string,
        key: request.key,
      });

      try {
        switch (request.method) {
          case 'storage:get': {
            const storedValue = window.localStorage.getItem(storageKey);
            if (storedValue === null) {
              postStorageResponse(
                createArcadeStorageResponseMessage({
                  type: 'arcade:storage-response',
                  requestId: request.requestId,
                  method: request.method,
                  ok: true,
                  found: false,
                }),
              );
              return;
            }

            postStorageResponse(
              createArcadeStorageResponseMessage({
                type: 'arcade:storage-response',
                requestId: request.requestId,
                method: request.method,
                ok: true,
                found: true,
                value: (() => {
                  const parsedValue = JSON.parse(storedValue);
                  if (!isArcadeStorageJson(parsedValue)) {
                    throw new Error('Stored value is not valid JSON');
                  }

                  return parsedValue;
                })(),
              }),
            );
            return;
          }
          case 'storage:set': {
            window.localStorage.setItem(
              storageKey,
              JSON.stringify(request.value),
            );
            postStorageResponse(
              createArcadeStorageResponseMessage({
                type: 'arcade:storage-response',
                requestId: request.requestId,
                method: request.method,
                ok: true,
              }),
            );
            return;
          }
          case 'storage:remove': {
            window.localStorage.removeItem(storageKey);
            postStorageResponse(
              createArcadeStorageResponseMessage({
                type: 'arcade:storage-response',
                requestId: request.requestId,
                method: request.method,
                ok: true,
              }),
            );
          }
        }
      } catch {
        postStorageResponse(
          createArcadeStorageResponseMessage({
            type: 'arcade:storage-response',
            requestId: request.requestId,
            method: request.method,
            ok: false,
            error: 'storage-unavailable',
          }),
        );
      }
    }

    window.addEventListener('message', handleStorageRequestMessage);
    return () => {
      window.removeEventListener('message', handleStorageRequestMessage);
    };
  }, [iframeRef, iframeOrigin, challengeId, gameId]);
}
